import { createFileRoute } from "@tanstack/react-router";
import { useLoginStatus } from "../../hooks/useLoginStatus";
import { useUserSettings } from "../../hooks/useUserSettings";
import { useUserSettingsUpdate } from "../../hooks/useUserSettingsUpdate";
import { useUserProfileUpdate } from "../../hooks/useUserProfileUpdate";
import { useForm } from "react-hook-form";
import { Toggle } from "../../components/Toggle";
import { getCsrfCookie } from "../../lib/utils";
import useTranslation from "../../hooks/useTranslation";
import { useEffect, useState } from "react";
import { useUserProfile } from "../../hooks/useUserProfile";
import { useWeatherLocationSuggestions } from "../../hooks/useWeatherLocationSuggestions";
import { TEXT_LANGUAGES } from "../../constants";

export const Route = createFileRoute("/app/settings")({
  component: SettingsPage,
});

function SettingsPage() {
  return <UserSettingsCard />;
}

type ProfileUpdateFormData = {
  pic: FileList;
};

function UserSettingsCard() {
  const loginStatus = useLoginStatus();

  const userSettings = useUserSettings();
  const userProfile = useUserProfile();
  const updateUserSettings = useUserSettingsUpdate();
  const updateProfile = useUserProfileUpdate();
  const { register, handleSubmit } = useForm<ProfileUpdateFormData>();
  const { translate } = useTranslation();
  const [weatherCity, setWeatherCity] = useState("");
  const [weatherCountryCode, setWeatherCountryCode] = useState("");
  const [weatherFeedback, setWeatherFeedback] = useState<
    "idle" | "success" | "error"
  >("idle");
  const [showWeatherSuggestions, setShowWeatherSuggestions] = useState(false);
  const serverWeatherCity = userSettings.data?.weather_city || "";
  const serverWeatherCountryCode = userSettings.data?.weather_country_code || "";
  const weatherSuggestionsQuery = useWeatherLocationSuggestions({
    query: weatherCity,
    countryCode: weatherCountryCode,
    enabled: showWeatherSuggestions,
  });
  const weatherSuggestions = weatherSuggestionsQuery.data?.results ?? [];

  useEffect(() => {
    if (!userSettings.isSuccess) {
      return;
    }
    setWeatherCity(serverWeatherCity);
    setWeatherCountryCode(serverWeatherCountryCode);
  }, [
    userSettings.isSuccess,
    serverWeatherCity,
    serverWeatherCountryCode,
  ]);

  const handleSaveWeatherLocation = async () => {
    try {
      await updateUserSettings.mutateAsync({
        userSettingsDto: {
          weather_city: weatherCity.trim(),
          weather_country_code: weatherCountryCode.trim().toUpperCase().slice(0, 2),
        },
      });
      setWeatherFeedback("success");
    } catch {
      setWeatherFeedback("error");
    }
    setTimeout(() => setWeatherFeedback("idle"), 2500);
  };

  if (
    !userSettings.isSuccess ||
    !loginStatus.isSuccess ||
    !userProfile.isSuccess
  ) {
    return <></>;
  }

  if (loginStatus.data.state === "logged out") {
    return <></>;
  }

  return (
    <div className="mx-4 max-w-md">
      <div className="pt-8 text-left text-xl font-bold leading-7 text-gray-600">
        <p>Hallo {loginStatus.data.username}!</p>
        <p className="text-sm text-gray-500">
          Gesamte Sitzungsdauer: {userProfile.data.total_session_duration}{" "}
          Sekunden
        </p>
      </div>
      <label className="block text-gray-700 mt-7 mb-1 text-left">
        AI model
      </label>
      <select
        className="w-full p-2 border rounded-sm md:m-0 text-base"
        value={userSettings.data.ai_model}
        onChange={(obj) => {
          console.log(obj.target.value);
          updateUserSettings.mutate({
            userSettingsDto: {
              ai_model: obj.target.value as "gpt-4o" | "gpt-4o-mini",
            },
          });
        }}
      >
        <option value="gpt-4o">Gpt 4o</option>
        <option value="gpt-4o-mini">GPT 4o mini</option>
      </select>
      <div className="mt-7 rounded-xl border border-slate-200 bg-white p-4">
        <label className="block text-left text-sm font-semibold text-slate-900">
          {translate("user_settings_form_label_app_language")}
        </label>
        <p className="mt-1 text-left text-xs text-slate-600">
          {translate("user_settings_form_hint_app_language")}
        </p>
        <select
          className="mt-3 w-full rounded-md border border-slate-300 p-3 text-base"
          value={userSettings.data.app_language}
          onChange={(obj) => {
            updateUserSettings.mutate({
              userSettingsDto: {
                app_language: obj.target.value as "ENGLISH" | "GERMAN",
              },
            });
          }}
        >
          <option value="ENGLISH">{translate("language_english")}</option>
          <option value="GERMAN">{translate("language_german")}</option>
        </select>
      </div>
      <div className="mt-4 rounded-xl border border-slate-200 bg-white p-4">
        <label className="block text-left text-sm font-semibold text-slate-900">
          {translate("user_settings_form_label_mother_tongue")}
        </label>
        <p className="mt-1 text-left text-xs text-slate-600">
          {translate("user_settings_form_hint_mother_tongue")}
        </p>
        <select
          className="mt-3 w-full rounded-md border border-slate-300 p-3 text-base"
          value={userSettings.data.mother_tongue}
          onChange={(obj) => {
            updateUserSettings.mutate({
              userSettingsDto: { mother_tongue: obj.target.value },
            });
          }}
        >
          {TEXT_LANGUAGES.map((language) => (
            <option key={language.id} value={language.id}>
              {translate(language.prettyTranslationKey)}
            </option>
          ))}
        </select>
      </div>
      <div className="mt-7 rounded-xl border border-slate-200 bg-white p-4">
        <p className="text-left text-sm font-semibold text-slate-900">
          {translate("weather_settings_section_title")}
        </p>
        <p className="mt-1 text-left text-xs text-slate-600">
          {translate("weather_settings_section_hint")}
        </p>
        <label className="mt-4 block text-left text-sm text-slate-700">
          {translate("weather_settings_city_label")}
        </label>
        <input
          type="text"
          value={weatherCity}
          onChange={(event) => setWeatherCity(event.target.value)}
          onFocus={() => setShowWeatherSuggestions(true)}
          onBlur={() => {
            // Delay closing so click selection can fire.
            setTimeout(() => setShowWeatherSuggestions(false), 120);
          }}
          placeholder={translate("weather_settings_city_placeholder")}
          className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-base focus:border-blue-500 focus:outline-hidden focus:ring-2 focus:ring-blue-200"
        />
        {showWeatherSuggestions && weatherCity.trim().length >= 2 && (
          <div className="mt-1 max-h-48 overflow-y-auto rounded-md border border-slate-200 bg-white">
            {weatherSuggestionsQuery.isLoading && (
              <p className="px-3 py-2 text-sm text-slate-500">
                {translate("weather_settings_suggestions_loading")}
              </p>
            )}
            {weatherSuggestionsQuery.isSuccess && weatherSuggestions.length === 0 && (
              <p className="px-3 py-2 text-sm text-slate-500">
                {translate("weather_settings_suggestions_empty")}
              </p>
            )}
            {weatherSuggestions.map((suggestion) => (
              <button
                key={`${suggestion.name}-${suggestion.country_code}-${suggestion.admin1}`}
                type="button"
                onClick={() => {
                  setWeatherCity(suggestion.name);
                  setWeatherCountryCode(suggestion.country_code);
                  setShowWeatherSuggestions(false);
                }}
                className="block w-full border-b border-slate-100 px-3 py-2 text-left text-sm text-slate-700 transition hover:bg-slate-50 last:border-b-0"
              >
                {suggestion.label}
              </button>
            ))}
          </div>
        )}
        <label className="mt-3 block text-left text-sm text-slate-700">
          {translate("weather_settings_country_label")}
        </label>
        <input
          type="text"
          value={weatherCountryCode}
          onChange={(event) => setWeatherCountryCode(event.target.value)}
          placeholder={translate("weather_settings_country_placeholder")}
          className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-base uppercase focus:border-blue-500 focus:outline-hidden focus:ring-2 focus:ring-blue-200"
          maxLength={2}
        />
        <button
          type="button"
          onClick={handleSaveWeatherLocation}
          disabled={updateUserSettings.isPending}
          className="mt-4 w-full rounded-md bg-blue-500 p-3 text-base font-semibold text-white transition duration-200 hover:bg-blue-600 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {updateUserSettings.isPending
            ? translate("weather_settings_saving")
            : translate("weather_settings_save")}
        </button>
        {weatherFeedback === "success" && (
          <p className="mt-2 text-left text-xs text-emerald-700">
            {translate("weather_settings_saved")}
          </p>
        )}
        {weatherFeedback === "error" && (
          <p className="mt-2 text-left text-xs text-rose-600">
            {translate("weather_settings_error")}
          </p>
        )}
      </div>

      <div className="block text-gray-700 mt-7 mb-1 text-left">Profilbild</div>
      <form
        onSubmit={handleSubmit((data) => {
          updateProfile.mutate({
            updateUserProfileDto: {
              pic: data.pic[0],
            },
          });
        })}
      >
        <label htmlFor="pic" className="cursor-pointer flex items-start">
          <input
            id="pic"
            type="file"
            accept="image/*"
            {...register("pic", {
              required: true,
            })}
          />
        </label>
        <input
          className="bg-slate-200 mb-6 rounded-xs p-3 mt-3 flex items-start cursor-pointer hover:bg-slate-300 transition duration-200"
          type="submit"
          value="Update Profile Pic"
        />
      </form>
      {loginStatus.data.is_staff && (
        <Toggle
          label="Show experimental features"
          value={userSettings.data.show_experimental_features}
          onChange={() => {
            updateUserSettings.mutate({
              userSettingsDto: {
                show_experimental_features:
                  !userSettings.data.show_experimental_features,
              },
            });
          }}
        />
      )}
      {loginStatus.data.is_staff && (
        <Toggle
          label="Show experimental text-to-speech"
          value={userSettings.data.show_experimental_speech_player}
          onChange={() => {
            updateUserSettings.mutate({
              userSettingsDto: {
                show_experimental_speech_player:
                  !userSettings.data.show_experimental_speech_player,
              },
            });
          }}
        />
      )}
      <Toggle
        label="Show welcome card"
        value={userSettings.data.show_welcome_card}
        onChange={() => {
          updateUserSettings.mutate({
            userSettingsDto: {
              show_welcome_card: !userSettings.data.show_welcome_card,
            },
          });
        }}
      />
      <div className="mt-8">
        <ProgressMail />
      </div>
    </div>
  );
}

function ProgressMail() {
  const { translate } = useTranslation();
  const [subscriptionState, setSubscriptionState] = useState<
    "idle" | "submitting" | "success" | "error"
  >("idle");

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
  } = useForm<{ email: string }>();

  const onSubmit = async (data: { email: string }) => {
    setSubscriptionState("submitting");

    try {
      const response = await fetch("/api/reeprise/emailsubscribers/", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRFTOKEN": getCsrfCookie(),
        },
        body: JSON.stringify({ email: data.email }),
      });

      if (response.ok) {
        setSubscriptionState("success");
        reset();
        // Reset success state after 3 seconds
        setTimeout(() => setSubscriptionState("idle"), 3000);
      } else {
        setSubscriptionState("error");
        // Reset error state after 5 seconds
        setTimeout(() => setSubscriptionState("idle"), 5000);
      }
    } catch {
      setSubscriptionState("error");
      setTimeout(() => setSubscriptionState("idle"), 5000);
    }
  };

  return (
    <div className="space-y-3">
      <label className="block text-gray-700 text-left font-medium">
        {translate("email_subscription_label")}
      </label>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-3">
        <div>
          <input
            {...register("email", {
              required: translate("email_subscription_required"),
              pattern: {
                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                message: translate("email_subscription_invalid"),
              },
            })}
            type="email"
            placeholder={translate("email_subscription_placeholder")}
            className={`w-full p-3 border rounded-md transition duration-200 ${
              errors.email
                ? "border-red-300 focus:border-red-500 focus:ring-red-200"
                : "border-gray-300 focus:border-blue-500 focus:ring-blue-200"
            } focus:outline-hidden focus:ring-2`}
            disabled={isSubmitting}
          />
          {errors.email && (
            <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
          )}
        </div>

        <button
          type="submit"
          disabled={isSubmitting || subscriptionState === "success"}
          className={`w-full p-3 rounded-md font-medium transition duration-200 ${
            subscriptionState === "success"
              ? "bg-green-500 text-white cursor-default"
              : subscriptionState === "error"
                ? "bg-red-500 hover:bg-red-600 text-white"
                : isSubmitting
                  ? "bg-gray-400 text-white cursor-not-allowed"
                  : "bg-blue-500 hover:bg-blue-600 text-white"
          }`}
        >
          {subscriptionState === "success" &&
            "✓ " + translate("email_subscription_success")}
          {subscriptionState === "error" &&
            translate("email_subscription_error")}
          {isSubmitting && translate("email_subscription_subscribing")}
          {subscriptionState === "idle" &&
            translate("email_subscription_subscribe")}
        </button>
      </form>
    </div>
  );
}
